import { Link } from "wouter";
import { motion } from "framer-motion";
import { SeoHead } from "@/components/SeoHead";
import { PageTransition } from "@/components/layout/PageTransition";
import { Trophy, Activity, ChevronRight, Shield } from "lucide-react";
import { useCms } from "@/hooks/useCms";

const ICON_MAP: Record<string, React.ElementType> = {
  Trophy, Activity
};

export default function JoinPage() {
  const { getMeta } = useCms();

  const heroMeta = getMeta<{ heading: string; subtext: string }>("join_hero", {
    heading: "YOUR NEXT CHAPTER STARTS HERE",
    subtext: "Whether you're an athlete ready to take your career to the next level or a coach looking to shape the next generation of champions — Eliada Sports Agency has a pathway for you."
  });

  const pathwaysMeta = getMeta<{ heading: string; subtext: string; items: any[] }>("join_pathways", {
    heading: "Choose Your Pathway",
    subtext: "Select the path that best describes you",
    items: [
      {
        type: "athlete",
        href: "/join/athlete",
        icon: "Trophy",
        label: "Join as an Athlete",
        tagline: "Get Represented",
        description: "Apply to have Eliada Sports Agency represent you professionally. We handle contracts, club placements, international transfers, sponsorships, and career development so you can focus on your game.",
        highlights: [
          "Professional representation & contracts",
          "Club placement & international transfers",
          "Career development & branding",
          "Sponsorship opportunities",
          "Trial arrangement",
        ],
        cta: "Apply as Athlete",
        color: "from-amber-500/20 to-transparent",
        border: "border-amber-500/30 hover:border-amber-500/70",
        iconBg: "bg-amber-500/10",
        iconColor: "text-amber-400",
        badge: "Athletes",
        badgeBg: "bg-amber-500/10 text-amber-400 border-amber-500/20",
        btnClass: "bg-primary text-[#0a0f1a] hover:bg-primary/90 hover:shadow-primary/30",
      },
      {
        type: "coach",
        href: "/join/coach",
        icon: "Activity",
        label: "Join as a Coach",
        tagline: "Shape Champions",
        description: "Are you an experienced coach, scout, or athlete development professional? Join the Eliada Sports Agency network and contribute your expertise to developing world-class athletes.",
        highlights: [
          "Work with elite athletes",
          "Academy & elite coaching roles",
          "International assignments",
          "Talent identification programs",
          "Performance & conditioning roles",
        ],
        cta: "Apply as Coach",
        color: "from-blue-500/20 to-transparent",
        border: "border-blue-500/30 hover:border-blue-500/70",
        iconBg: "bg-blue-500/10",
        iconColor: "text-blue-400",
        badge: "Coaching Staff",
        badgeBg: "bg-blue-500/10 text-blue-400 border-blue-500/20",
        btnClass: "bg-blue-500 text-white hover:bg-blue-400 hover:shadow-blue-500/30",
      }
    ]
  });

  return (
    <>
      <SeoHead
        title="Join the Agency"
        description="Join Eliada Sports Agency as an athlete or coach. Apply for professional representation, club placements, international transfers, or coaching opportunities."
        path="/join"
      />
      <PageTransition className="pt-24 pb-24 bg-[#070b13] min-h-screen">

        {/* ── Hero ── */}
        <div className="relative py-20 overflow-hidden border-b border-white/5 bg-secondary/20">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-blue-500/5 opacity-70" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:14px_24px]" />
          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-1.5 text-xs text-primary font-bold uppercase tracking-wider bg-primary/10 px-3 py-1.5 rounded-full border border-primary/20 mb-5"
            >
              <Shield size={12} /> Join the Agency
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl sm:text-5xl md:text-6xl font-display font-extrabold text-white tracking-tight mb-5"
            >
              {heroMeta.heading.split(" ").map((w, i, arr) => 
                i >= arr.length - 2 ? <span key={i} className="text-primary">{w} </span> : <span key={i}>{w} </span>
              )}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="text-lg text-white/60 max-w-2xl mx-auto leading-relaxed font-light"
            >
              {heroMeta.subtext}
            </motion.p>
          </div>
        </div>

        {/* ── Pathway Cards ── */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-display font-bold text-white mb-3">
              {pathwaysMeta.heading}
            </h2>
            <p className="text-white/50 text-sm">{pathwaysMeta.subtext}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {pathwaysMeta.items.map((p, i) => {
              const Icon = ICON_MAP[p.icon] ?? Trophy;
              return (
              <motion.div
                key={p.type}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + i * 0.1 }}
                className={`relative bg-[#0d1524] border rounded-3xl overflow-hidden transition-all duration-300 group hover:-translate-y-1 hover:shadow-2xl ${p.border}`}
              >
                {/* gradient bg */}
                <div className={`absolute inset-0 bg-gradient-to-br ${p.color} opacity-50 pointer-events-none`} />

                <div className="relative p-8 flex flex-col h-full">
                  {/* Badge + Icon */}
                  <div className="flex items-start justify-between mb-6">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${p.iconBg}`}>
                      <Icon size={28} className={p.iconColor} />
                    </div>
                    <span className={`text-xs font-bold px-3 py-1 rounded-full border ${p.badgeBg}`}>
                      {p.badge}
                    </span>
                  </div>

                  {/* Text */}
                  <p className="text-xs font-bold uppercase tracking-wider text-white/40 mb-1">{p.tagline}</p>
                  <h3 className="text-2xl font-display font-bold text-white mb-4 group-hover:text-primary transition-colors">
                    {p.label}
                  </h3>
                  <p className="text-sm text-white/60 leading-relaxed mb-6">{p.description}</p>

                  {/* Highlights */}
                  <ul className="space-y-2 mb-8 flex-grow">
                    {p.highlights.map((h: string) => (
                      <li key={h} className="flex items-center gap-2.5 text-sm text-white/70">
                        <ChevronRight size={14} className={p.iconColor + " shrink-0"} />
                        {h}
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <Link
                    href={p.href}
                    className={`inline-flex items-center justify-center gap-2 font-display font-bold text-sm px-8 py-4 rounded-2xl transition-all hover:shadow-lg hover:scale-105 active:scale-95 ${p.btnClass}`}
                  >
                    {p.cta} <ChevronRight size={16} />
                  </Link>
                </div>
              </motion.div>
            )})}
          </div>
        </div>

        {/* ── Bottom Note ── */}
        <div className="max-w-2xl mx-auto px-4 text-center pb-8">
          <p className="text-xs text-white/25 leading-relaxed">
            Applications are reviewed within 5–7 business days. Submitting an application does not
            guarantee representation, employment, or engagement with Eliada Sports Agency.
          </p>
        </div>
      </PageTransition>
    </>
  );
}
